import API_BASE from '../config';
import {
  getTourProgress,
  setTourProgress,
  type TourProgressEntry,
  type TourProgressMap,
  type TourProgressStatus,
} from '../utils/userPreferences';
import type { TourFlowMode } from './types';

export async function persistTourStatus(
  tourId: string,
  status: TourProgressStatus,
  opts?: { lastStep?: number; mode?: TourFlowMode; replace?: boolean; extra?: TourProgressMap }
): Promise<TourProgressMap> {
  const entry: TourProgressEntry = {
    status,
    at: new Date().toISOString(),
    ...(typeof opts?.lastStep === 'number' ? { lastStep: opts.lastStep } : {}),
    ...(opts?.mode ? { mode: opts.mode } : {}),
  };

  const payload: TourProgressMap = {
    ...(opts?.extra || {}),
    [tourId]: entry,
  };

  const res = await fetch(`${API_BASE}/api/users/me/preferences`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      tourProgress: payload,
      ...(opts?.replace ? { tourProgressReplace: true } : {}),
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed to save tour progress');

  const merged: TourProgressMap = opts?.replace
    ? { ...(data.tourProgress || payload) }
    : {
        ...getTourProgress(),
        ...(data.tourProgress || payload),
      };
  setTourProgress(merged);

  try {
    const raw = localStorage.getItem('user');
    if (raw) {
      const user = JSON.parse(raw);
      user.tourProgress = merged;
      localStorage.setItem('user', JSON.stringify(user));
    }
  } catch {
    /* ignore */
  }

  return merged;
}

export async function logTourEvent(payload: {
  type: 'TOUR_STARTED' | 'TOUR_STEP' | 'TOUR_COMPLETED' | 'TOUR_SKIPPED';
  tourId: string;
  stepIndex?: number;
  role?: string | null;
  platform?: 'web' | 'mobile';
}) {
  try {
    await fetch(`${API_BASE}/api/activity/tour`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        type: payload.type,
        tourId: payload.tourId,
        stepIndex: payload.stepIndex,
        role: payload.role || undefined,
        platform: payload.platform || 'web',
      }),
    });
  } catch {
    /* best-effort */
  }
}
